import { useNavigate } from 'react-router-dom';
import StatusBadge from '@/components/base/StatusBadge';
import Badge from '@/components/base/Badge';
import ProgressBar from '@/components/base/ProgressBar';
import PlatformIcon from '@/components/base/PlatformIcon';
import { formatMoney, formatDate } from '@/utils/format';
import type { Campaign } from '@/types/portal';

export default function CampaignTable({ rows }: { rows: Campaign[] }) {
  const navigate = useNavigate();

  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-14 text-center">
        <span className="w-12 h-12 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 text-xl">
          <i className="ri-megaphone-line" />
        </span>
        <h3 className="mt-3 font-heading text-base font-semibold text-foreground-950">
          No campaigns match
        </h3>
        <p className="mt-1 max-w-sm text-sm text-foreground-600">
          Try another status or clear the market filter.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-background-200 bg-background-50">
      <table className="row-accent w-full min-w-[920px] border-collapse text-left">
        <thead>
          <tr className="border-b border-background-200 text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-400">
            <th className="px-5 py-3 font-semibold">Campaign</th>
            <th className="px-3 py-3 font-semibold">Status</th>
            <th className="px-3 py-3 font-semibold">Progress</th>
            <th className="px-3 py-3 font-semibold">Creators</th>
            <th className="px-3 py-3 font-semibold">Spend / budget</th>
            <th className="px-3 py-3 font-semibold">Timeline</th>
            <th className="px-5 py-3 text-right font-semibold">Flags</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((campaign) => (
            <tr
              key={campaign.id}
              onClick={() => navigate(`/campaigns/${campaign.id}`)}
              className="cursor-pointer border-b border-background-100 last:border-0 hover:bg-background-100"
            >
              <td className="px-5 py-3.5">
                <p className="text-sm font-medium text-foreground-950">{campaign.name}</p>
                <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
                  <span>{campaign.brandClient}</span>
                  <span className="text-foreground-300">·</span>
                  <span className="inline-flex items-center gap-1.5">
                    {campaign.platforms.map((platform) => (
                      <PlatformIcon key={platform} platform={platform} className="text-[13px]" />
                    ))}
                  </span>
                </p>
              </td>
              <td className="px-3 py-3.5">
                <StatusBadge status={campaign.status} />
              </td>
              <td className="px-3 py-3.5">
                <ProgressBar value={campaign.progress} showLabel className="w-32" />
              </td>
              <td className="px-3 py-3.5 text-sm text-foreground-700">
                {campaign.creatorsActive}
                <span className="text-foreground-400">/{campaign.creatorsTotal || '—'}</span>
              </td>
              <td className="px-3 py-3.5 text-sm text-foreground-700">
                {formatMoney(campaign.spend, campaign.currency)}
                <span className="block text-[11px] text-foreground-400">
                  of {formatMoney(campaign.budget, campaign.currency)}
                </span>
              </td>
              <td className="px-3 py-3.5 text-xs text-foreground-600">
                {formatDate(campaign.startDate)}
                <span className="block text-foreground-400">to {formatDate(campaign.endDate)}</span>
              </td>
              <td className="px-5 py-3.5 text-right">
                {campaign.flags.length > 0 ? (
                  <Badge tone="primary" icon="ri-flag-line">
                    {campaign.flags[0]}
                  </Badge>
                ) : (
                  <span className="text-xs text-foreground-400">Clear</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}